import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  CreationRequest,
  CreationResponse,
  CreationResponseDto,
  NewRequestCreationDto,
} from "@fiduciaria/RequestCreation/Types/Interfaces/new-request-creation.interface";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { Cacheable } from "ts-cacheable";

import { environment } from "@environments/environment";

import { ParamsService } from "@shared/Services/params.service";

import {
  BizagiOption,
  StageDto,
  Currency,
  CurrencyDto,
  RequestStatus,
  RequestStatusDto,
  IdentificationTypesDto,
  IdentificationType,
  CustomerTypesDto,
} from "../Types/bizagi-option.interface";
import { Dependency, DependenciesDto } from "../Types/dependency.interface";
import { StageCode } from "../Types/Enums/stage-code.enum";
import { AdditionalField, Escrow, EscrowDto } from "../Types/escrow.interface";
import { TrustRequest } from "../Types/request.interface";
import { TrustRequestsFilter } from "../Types/requests-filter.interface";
import { RequiredDocument, RequiredDocumentDTO, RequiredDocumentsFilter } from "../Types/required-document.interface";
import { RequiredInfo } from "../Types/required-info.interface";
import { TrustSubType, TrustSubTypeDTO, TrustType, TrustTypeDTO } from "../Types/type.interface";

const { enlineaBackendUrl } = environment;

@Injectable()
export class TrustRepository {
  constructor(private http: HttpClient, private paramsService: ParamsService) {}

  @Cacheable()
  public getEscrows(): Observable<Escrow[]> {
    return this.http.get<EscrowDto>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/Escrows`).pipe(map(({ pgIdent }) => pgIdent));
  }
  
  @Cacheable()
  public getEscrowsAll(): Observable<Escrow[]> {
    return this.http.get<EscrowDto>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/Escrows/All`).pipe(map(({ pgIdent }) => pgIdent));
  }

  @Cacheable()
  public getDependencies(): Observable<Dependency[]> {
    return this.http
      .get<DependenciesDto>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/Dependencies`)
      .pipe(map(({ dependencia }) => dependencia));
  }

  @Cacheable()
  public getTypes(dependencyId: string): Observable<TrustType[]> {
    return this.http
      .get<TrustTypeDTO>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/${dependencyId}/Types`)
      .pipe(map(({ idSGralTipoSolicitud }) => idSGralTipoSolicitud));
  }

  @Cacheable()
  public getSubTypes(typeId: string): Observable<TrustSubType[]> {
    return this.http
      .get<TrustSubTypeDTO>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/Types/${typeId}/SubTypes`)
      .pipe(map((res) => res?.idSubTipoSolicitud || []));
  }

  @Cacheable()
  public getStages(): Observable<BizagiOption[]> {
    return this.http.get<StageDto>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/Stages`).pipe(map(({ sEtpa }) => sEtpa));
  }

  @Cacheable()
  public getRequestStatus(): Observable<RequestStatus[]> {
    return this.http
      .get<RequestStatusDto>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/RequestStatus`)
      .pipe(map(({ estadoSolicitud }) => estadoSolicitud));
  }

  @Cacheable()
  public getCurrencies(): Observable<Currency[]> {
    return this.http
      .get<CurrencyDto>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/Currencies`)
      .pipe(map(({ leTipoMoneda }) => leTipoMoneda));
  }

  @Cacheable()
  public getCustomerTypes(): Observable<BizagiOption[]> {
    return this.http
      .get<CustomerTypesDto>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/FilteredCustomer`)
      .pipe(map(({ leTipoTercero }) => leTipoTercero));
  }

  @Cacheable()
  public getIdentificationTypes(): Observable<IdentificationType[]> {
    return this.http
      .get<IdentificationTypesDto>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/IdentificationTypes`)
      .pipe(map(({ tipoID }) => tipoID));
  }

  @Cacheable()
  public getRequiredDocuments({ dependecyId, typeId, subTypeId }: RequiredDocumentsFilter): Observable<RequiredDocument[]> {
    return this.http
      .get<RequiredDocumentDTO>(
        `${enlineaBackendUrl}/api/bizagi/Fiduciary/Escrows/${dependecyId}/Types/${typeId}/SubTypes/${subTypeId}/RequireDocuments`
      )
      .pipe(map((response) => response?.pgDocsReqs || []));
  }

  @Cacheable()
  public getRequiredInfo(subTypeId: string): Observable<RequiredInfo> {
    return this.http.get<RequiredInfo>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/SubTypes/${subTypeId}/RequireInfo`);
  }

  @Cacheable()
  public getAdditionalRequiredFields(subTypeId: string): Observable<AdditionalField[]> {
    return this.http.get<AdditionalField[]>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/SubTypes/${subTypeId}/RequiredFields`);
  }

  public createTrustRequest(creationRequest: CreationRequest): Observable<CreationResponse> {
    const newRequestCreationDto: NewRequestCreationDto = {
      applicant: {
        escrowsRNC: creationRequest.escrow.sid,
        escrowsId: creationRequest.escrow.sCodigo,
        dependecyId: creationRequest.area.sCodigo,
        requestTypeId: creationRequest.type.sCodigo,
        requestSubTypeId: creationRequest.subtype.sCodigo,
        escrowsTypeId: creationRequest.escrow.idTipoFideicomiso.sCodigo,
      },
      stageId: creationRequest?.stage?.sCodigo || StageCode.DontApply,
      description: creationRequest.description,
      files: creationRequest.files
        .filter(({ file }) => Boolean(file))
        .map(({ name, file }) => ({
          documentTypeName: name,
          file: {
            file: file.buffer,
            fileExtension: file.name.split(".").pop(),
            name: file.name,
          },
        })),
      amount: creationRequest.amount,
      currencyId: creationRequest?.currency,
      customerName: creationRequest.customerName,
      customerType: creationRequest?.customerType?.sCodigo,
      identificationType: creationRequest?.identificationType?.sCodigo,
      invoiceQuantity: creationRequest.invoiceQuantity,
      identification: creationRequest.identification,
      informations: creationRequest.informations,
    };
    return this.http
      .post<CreationResponseDto>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/Solicitud`, newRequestCreationDto)
      .pipe(map(({ process }) => process));
  }

  @Cacheable()
  public getTrustRequests(filter: TrustRequestsFilter): Observable<TrustRequest[]> {
    const params = this.paramsService.mapRequestParams(filter);
    return this.http.get<TrustRequest[]>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/TrustApplications`, { params });
  }

  public getTrustApplicationsByUserEscrowsRequests(filter: TrustRequestsFilter): Observable<TrustRequest[]> {
    const params = this.paramsService.mapRequestParams(filter);
    return this.http.get<TrustRequest[]>(`${enlineaBackendUrl}/api/fiduciaria/FiduciariaUsuarios/TrustApplicationsByUserEscrows`, { params });
  }

  public getTrustRequestsAlways(filter: TrustRequestsFilter): Observable<TrustRequest[]> {
    const params = this.paramsService.mapRequestParams(filter);
    return this.http.get<TrustRequest[]>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/TrustApplications`, { params });
  }
}
